/**
 * 入口文件
 * 初始化应用
 */

import { initRouter } from './app/router.js';
import { isMobile, getDeviceTier } from './app/utils.js';

// 导入样式
import './styles/main.css';
import './styles/timeline.css';
import './styles/detail.css';

/**
 * 预加载关键资源
 * 首屏背景图 + 字体，确保加载完成后再展示内容
 */
function preloadAssets(onProgress, onComplete) {
  // 按优先级排序：首屏背景图最先加载
  const assets = [
    { type: 'image', src: 'images/bg-pre_qin.webp', name: '先秦展厅' },
    { type: 'image', src: 'images/bg-han.webp', name: '汉代展厅' },
    { type: 'image', src: 'images/bg-wei_jin.webp', name: '魏晋展厅' },
    { type: 'image', src: 'images/bg-song.webp', name: '宋代展厅' },
    { type: 'image', src: 'images/bg-yuan.webp', name: '元代展厅' },
    { type: 'image', src: 'images/bg-ming.webp', name: '明代展厅' },
    { type: 'image', src: 'images/bg-qing.webp', name: '清代展厅' },
    { type: 'image', src: 'images/bg-xihu_silk.webp', name: '民国展厅' },
    { type: 'image', src: 'images/bg-minguo.webp', name: '展厅布置' },
    { type: 'font', src: 'fonts/qiji-subset.woff2', name: '字体' },
  ];

  let loaded = 0;
  const total = assets.length;
  const hints = ['正在筹备展厅…', '正在展开画卷…', '正在布置展柜…', '即将入馆…'];

  function updateProgress() {
    loaded++;
    const percent = Math.round((loaded / total) * 100);
    const hintIndex = Math.min(Math.floor((loaded / total) * hints.length), hints.length - 1);
    onProgress(percent, hints[hintIndex]);

    if (loaded >= total) {
      setTimeout(onComplete, 300); // 短暂停顿让进度条到 100%
    }
  }

  // 同时加载所有资源（不阻塞）
  assets.forEach(asset => {
    if (asset.type === 'image') {
      const img = new Image();
      img.onload = updateProgress;
      img.onerror = updateProgress; // 出错也继续，不卡死
      img.src = asset.src;
    } else if (asset.type === 'font') {
      const fontFace = new FontFace('Qiji', `url(${asset.src})`);
      fontFace.load()
        .then(() => { document.fonts.add(fontFace); updateProgress(); })
        .catch(() => { updateProgress(); });
    }
  });

  // 保底：最多 8 秒后强制完成（防止某个资源卡住）
  setTimeout(onComplete, 8000);
}

/**
 * 隐藏加载界面，展示内容
 */
function hideLoadingScreen() {
  const loader = document.getElementById('loading-screen');
  if (loader) {
    loader.classList.add('hidden');
    setTimeout(() => loader.remove(), 1000);
  }
}

/**
 * 更新加载进度
 */
function updateLoadingUI(percent, hint) {
  const bar = document.getElementById('loading-bar-fill');
  const hintEl = document.getElementById('loading-hint');
  if (bar) bar.style.width = percent + '%';
  if (hintEl) hintEl.textContent = hint;
}

/**
 * 应用初始化
 */
function init() {
  console.log('🌂 伞韵 — 中国伞文化交互展');
  console.log(`设备类型: ${isMobile() ? '移动端' : '桌面端'}`);
  console.log(`性能等级: ${getDeviceTier()}`);

  // 初始化路由系统（先准备 DOM 结构，但不显示）
  initRouter();

  // 添加全局样式变量（安全区域）
  document.documentElement.style.setProperty('--sat', '0px');
  document.documentElement.style.setProperty('--sar', '0px');
  document.documentElement.style.setProperty('--sab', '0px');
  document.documentElement.style.setProperty('--sal', '0px');

  // 预加载资源，完成后显示内容
  preloadAssets(updateLoadingUI, hideLoadingScreen);
}

// DOM 就绪后初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
